/**
 * Utils.ts
 * @author  Abhilash Panwar (abpanwar) Hector Hernandez (hectorh)
 * @copyright Microsoft 2018
 * File containing utility functions.
 */
import { strShimObject } from "@microsoft/applicationinsights-shims";
import { IEventProperty, IExtendedTelemetryItem } from "./DataModels";
import { EventPropertyType, _ExtendedInternalMessageId, GuidStyle, ValueKind, FieldValueSanitizerType } from "./Enums";
import {
    CoreUtils, ITelemetryItem, getDocument, hasNavigator, getNavigator, getWindow, getLocation, getGlobalInst,
    isReactNative as envUtilsIsReactNative, objForEachKey,
} from "@microsoft/applicationinsights-core-js";

export const Version = '3.0.2';
export const FullVersionString = "1DS-Web-JS-" + Version;
export const strUndefined = "undefined";
export const strObject = "object";

const Prototype = "prototype";

// If value is array just get the type for the first element
const _fieldTypeEventPropMap = {
    [FieldValueSanitizerType.NotSet]: EventPropertyType.Unspecified,
    [FieldValueSanitizerType.Number]: EventPropertyType.Double,
    [FieldValueSanitizerType.String]: EventPropertyType.String,
    [FieldValueSanitizerType.Boolean]: EventPropertyType.Bool,
    [FieldValueSanitizerType.Array | FieldValueSanitizerType.Number]: EventPropertyType.Double,
    [FieldValueSanitizerType.Array | FieldValueSanitizerType.String]: EventPropertyType.String,
    [FieldValueSanitizerType.Array | FieldValueSanitizerType.Boolean]: EventPropertyType.Bool,
};

/**
 * @ignore
 */
let _uaDisallowsSameSiteNone = null;

/**
 * @ignore
 */
const _isUndefined = CoreUtils.isUndefined;

/**
 * @ignore
 */
const _isNullOrUndefined = CoreUtils.isNullOrUndefined;

/**
 * @ignore
 */
function _isNotUndefined(value: any): boolean {
    return !_isUndefined(value);
}

/**
 * @ignore
 */
let _hasOwnProperty = CoreUtils.hasOwnProperty;

var beaconsSupported: boolean | null = null;
var uInt8ArraySupported: boolean | null = null;
var _areCookiesAvailable: boolean | undefined;

export let arrForEach = CoreUtils.arrForEach;
export let arrIndexOf = CoreUtils.arrIndexOf;
export let arrMap = CoreUtils.arrMap;
export let arrReduce = CoreUtils.arrReduce;
export let objKeys = CoreUtils.objKeys;
export let toISOString = CoreUtils.toISOString;
export let isReactNative = envUtilsIsReactNative;
export let isString = CoreUtils.isString;
export let isNumber = CoreUtils.isNumber;
export let isBoolean = CoreUtils.isBoolean;
export let isFunction = CoreUtils.isFunction;
export let isArray = CoreUtils.isArray;
export let isObject = CoreUtils.isObject;

/**
 * helper method to trim strings (IE8 does not implement String.prototype.trim)
 */
export let strTrim = CoreUtils.strTrim;

/**
 * Checks if document object is available
 */
export var isDocumentObjectAvailable: boolean = Boolean(getDocument());

/**
 * Checks if window object is available
 */
export var isWindowObjectAvailable: boolean = Boolean(getWindow());

/**
 * Checks if value is assigned to the given param.
 * @param value - The token from which the tenant id is to be extracted.
 * @returns True/false denoting if value is assigned to the param.
 */
export function isValueAssigned(value: any) {
    /// <summary> takes a value and checks for undefined, null and empty string </summary>
    /// <param type="any"> value to be tested </param>
    /// <returns> true if value is null undefined or emptyString </returns>
    return !(_isNullOrUndefined(value) || value === "");
}

/**
 * Gets the tenant id from the tenant token.
 * @param apiKey - The token from which the tenant id is to be extracted.
 * @returns The tenant id.
 */
export function getTenantId(apiKey: string | undefined): string {
    if (apiKey) {
        let indexTenantId = apiKey.indexOf("-");
        if (indexTenantId > -1) {
            return apiKey.substring(0, indexTenantId);
        }
    }
    return "";
}

/**
 * Checks if HTML5 Beacons are supported in the current environment.
 * @returns True if supported, false otherwise.
 */
export function isBeaconsSupported(): boolean {
    if (beaconsSupported === null) {
        beaconsSupported = hasNavigator() && Boolean(getNavigator().sendBeacon);
    }
    return beaconsSupported;
}

/**
 * Checks if Uint8Array are available in the current environment. Safari and Firefox along with
 * ReactNative are known to not support Uint8Array properly.
 * @returns True if available, false otherwise.
 */
export function isUint8ArrayAvailable(): boolean {
    if (uInt8ArraySupported === null) {
        uInt8ArraySupported = _isNotUndefined(Uint8Array) && !isSafariOrFirefox() && !isReactNative();
    }
    return uInt8ArraySupported;
}

/**
 * Checks if the value is a valid EventLatency.
 * @param value - The value that needs to be checked.
 * @returns True if the value is in AWTEventLatency, false otherwise.
 */
export function isLatency(value: number | undefined): boolean {
    if (value && isNumber(value) && value >= 1 && value <= 3) {
        return true;
    }
    return false;
}

/**
 * Sanitizes the Property. It checks the that the property name and value are valid. It also
 * checks/populates the correct type and pii of the property value.
 * @param name - property name                          - The property name.
 * @param property - The property value or an IEventProperty containing value,
 * type ,pii and customer content.
 * @returns IEventProperty containing valid name, value, pii and type or null if invalid.
 */
export function sanitizeProperty(name: string,
                                 property: string | number | boolean | string[] | number[] | boolean[] | object | IEventProperty,
                                 stringifyObjects?: boolean): IEventProperty | null {
    // Check that property is valid
    if (!isString(name) || _isNullOrUndefined(property) || property === "") {
        return null;
    }
    // If the property isn't IEventProperty (and is either string, number, boolean or array), convert it into one.
    if (isString(property) || isNumber(property) || isBoolean(property) || isArray(property)) {
        property = ({ value: property } as IEventProperty);
    } else if (typeof property === "object" && !property.hasOwnProperty("value")) {
        property = ({ value: stringifyObjects ? JSON.stringify(property) : property } as IEventProperty);
    } else if (_isNullOrUndefined((property as IEventProperty).value)
        || (property as IEventProperty).value === "" || (!isString((property as IEventProperty).value)
            && !isNumber((property as IEventProperty).value) && !isBoolean((property as IEventProperty).value)
            && !isArray((property as IEventProperty).value))) {
        // Since property is IEventProperty, we need to validate its value
        return null;
    }

    // We need to check that if the property value is an array, it is valid
    if (isArray((property as IEventProperty).value) &&
        !isArrayValid((property as IEventProperty).value as string[] | number[] | boolean[])) {
        return null;
    }

    // If either pii or cc is set convert value to string (since only string pii/cc is allowed).
    // If the value is a complex type like an array that can't be converted to string we will drop
    // the property.
    if (!_isNullOrUndefined((property as IEventProperty).kind)) {
        if (isArray((property as IEventProperty).value) || !isValueKind((property as IEventProperty).kind)) {
            return null;
        }

        (property as IEventProperty).value = (property as IEventProperty).value.toString();
    }

    return (property as IEventProperty);
}

/**
 * Converts a date object into an ISO string. This is needed because not all browsers support ISO string format
 * on the date.
 * @param date - The date which needs to be converted to ISO format.
 * @returns The date in ISO format.
 */
export function getISOString(date: Date): string {
    return date.getUTCFullYear() + "-" +
        twoDigit(date.getUTCMonth() + 1) + "-" +
        twoDigit(date.getUTCDate()) + "T" +
        twoDigit(date.getUTCHours()) + ":" +
        twoDigit(date.getUTCMinutes()) + ":" +
        twoDigit(date.getUTCSeconds()) + "." +
        threeDigit(date.getUTCMilliseconds()) + "Z";
}

export function useXDomainRequest(): boolean | undefined {

    if (typeof XMLHttpRequest !== undefined) {
        const xhr: any = getGlobalInst("XMLHttpRequest");
        if (xhr) {
            const conn = new xhr();
            return Boolean(_isUndefined(conn.withCredentials) && (typeof XDomainRequest !== undefined));
        }

    }
}

export function getCommonSchemaMetaData(value: string | boolean | number | string[] | number[] | boolean[] | undefined, kind: number | undefined, type?: number | undefined): number {
    let encodedTypeValue = -1;

    if (!_isUndefined(value)) {
        if (kind > 0) {
            if (kind === 32) {
                // encode customer content. Value can only be string. bit 13-16 are for cc
                encodedTypeValue = (1 << 13);
            } else if (kind <= 13) {
                // encode PII. Value can only be string. bits 5-12 are for Pii
                encodedTypeValue = (kind << 5);
            }
        }

        // isDataType checks that the "type" is a number so we don't need to check for undefined
        if (isDataType(type)) {
            // Data Type is provided and valid, so use that
            if (encodedTypeValue === -1) {
                // Don't return -1
                encodedTypeValue = 0;
            }

            encodedTypeValue |= type;
        } else {
            let propType = _fieldTypeEventPropMap[getFieldValueType(value)] || -1;

            if (encodedTypeValue !== -1 && propType !== -1) {
                // pii exists so we must return correct type
                encodedTypeValue |= propType;
            } else if (propType === EventPropertyType.Double) {
                encodedTypeValue = propType;
            }
        }
    }

    return encodedTypeValue;
}

export function cookieAvailable(): boolean {
    if (_areCookiesAvailable === undefined) {
        _areCookiesAvailable = false;

        try {
            let doc = getDocument();
            let nav = getNavigator();
            if (doc && !_isUndefined(doc.cookie) && nav && nav.cookieEnabled) {
                _areCookiesAvailable = true;
            }
        } catch (e) {
            // Unexpected, but just swallow
        }
    }

    return _areCookiesAvailable;
}

export function disallowsSameSiteNone(userAgent: string) {
    if (!isString(userAgent)) {
        return false;
    }

    // Cover all iOS based browsers here. This includes:
    // - Safari on iOS 12 for iPhone, iPod Touch, iPad
    // - WkWebview on iOS 12 for iPhone, iPod Touch, iPad
    // - Chrome on iOS 12 for iPhone, iPod Touch, iPad
    // All of which are broken by SameSite=None, because they use the iOS networking stack
    if (userAgent.indexOf("CPU iPhone OS 12") !== -1 || userAgent.indexOf("iPad; CPU OS 12") !== -1) {
        return true;
    }

    // Cover Mac OS X based browsers that use the Mac OS networking stack. This includes:
    // - Safari on Mac OS X
    // This does not include:
    // - Internal browser on Mac OS X
    // - Chrome on Mac OS X
    // - Chromium on Mac OS X
    // Because they do not use the Mac OS networking stack.
    if (userAgent.indexOf("Macintosh; Intel Mac OS X 10_14") !== -1 && userAgent.indexOf("Version/") !== -1 && userAgent.indexOf("Safari") !== -1) {
        return true;
    }

    // Cover Mac OS X internal browsers that use the Mac OS networking stack. This includes:
    // - Internal browser on Mac OS X
    // This does not include:
    // - Safari on Mac OS X
    // - Chrome on Mac OS X
    // - Chromium on Mac OS X
    // Because they do not use the Mac OS networking stack.
    if (userAgent.indexOf("Macintosh; Intel Mac OS X 10_14") !== -1 && strEndsWith(userAgent, "AppleWebKit/605.1.15 (KHTML, like Gecko)")) {
        return true;
    }

    // Cover Chrome 50-69, because some versions are broken by SameSite=None, and none in this range require it.
    // Note: this covers some pre-Chromium Edge versions, but pre-Chromim Edge does not require SameSite=None, so this is fine.
    // Note: this regex applies to Windows, Mac OS X, and Linux, deliberately.
    if (userAgent.indexOf("Chrome/5") !== -1 || userAgent.indexOf("Chrome/6") !== -1) {
        return true;
    }

    // Unreal Engine runs Chromium 59, but does not advertise as Chrome until 4.23. Treat versions of Unreal
    // that don't specify their Chrome version as lacking support for SameSite=None.
    if (userAgent.indexOf("UnrealEngine") !== -1 && userAgent.indexOf("Chrome") === -1) {
        return true;
    }

    // UCBrowser < 12.13.2 ignores Set-Cookie headers with SameSite=None
    // NB: this rule isn't complete - you need regex to make a complete rule.
    // See: https://www.chromium.org/updates/same-site/incompatible-clients
    if (userAgent.indexOf("UCBrowser/12") !== -1 || userAgent.indexOf("UCBrowser/11") !== -1) {
        return true;
    }

    return false;
}

/**
 * Sets the value of a cookie.
 * @param name - Cookie name.
 * @param value - Cookie value.
 * @param days - Expiration days.
 */
export function setCookie(name: string, value: string, days: number): void {
    if (cookieAvailable()) {
        var secureAttrib = "";

        let loc = getLocation();
        if (loc && loc.protocol === "https:") {
            secureAttrib = ";secure";
            if (_uaDisallowsSameSiteNone === null) {
                _uaDisallowsSameSiteNone = disallowsSameSiteNone((getNavigator() || {} as Navigator).userAgent);
            }

            if (!_uaDisallowsSameSiteNone) {
                value = value + ";SameSite=None"; // SameSite can only be changed on secure pages and browsers that support samesite=None setting
            }
        }

        let date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        let expires = "; expires=" + date.toUTCString();
        getDocument().cookie = name + "=" + value + expires + "; path=/" + secureAttrib;
    }
}

/**
 * Deletes a cookie, by setting its expiration to -1.
 * @param name - Cookie name to delete.
 */
export function deleteCookie(name: string): void {
    setCookie(name, "", -1);
}

/**
 * Gets the cookie value for the specified cookie.
 * if value is k1=v1&k2==v2 then will return 'v1' for key 'k1'
 * @param cookieName - Cookie name.
 */
export function getCookie(name: string): string {
    if (cookieAvailable()) {
        name = name + "=";
        var decodedCookie = decodeURIComponent(((getDocument() || ({} as Document)).cookie) || "");
        var ca = decodedCookie.split(";");
        for (var i = 0; i < ca.length; i++) {
            var c = ca[i];
            let j = 0;
            while (c.charAt(j) === " ") {
                j++;
            }
            c = c.substring(j);
            if (c.indexOf(name) === 0) {
                return c.substring(name.length, c.length);
            }
        }
    }
    return "";
}

/**
 * Create a new guid.
 * @param style - The style of guid to generated, defaults to Digits
 * Digits (Default) : 32 digits separated by hyphens: 00000000-0000-0000-0000-000000000000
 * Braces - 32 digits separated by hyphens, enclosed in braces: {00000000-0000-0000-0000-000000000000}
 * Parentheses - 32 digits separated by hyphens, enclosed in parentheses: (00000000-0000-0000-0000-000000000000)
 * Numeric - 32 digits: 00000000000000000000000000000000
 * @returns The formatted guid.
 */
export function createGuid(style: GuidStyle = GuidStyle.Digits): string {
    let theGuid = CoreUtils.newGuid();
    if (style === GuidStyle.Braces)  {
        theGuid = "{" + theGuid + "}";
    } else if (style === GuidStyle.Parentheses) {
        theGuid = "(" + theGuid + ")";
    } else if (style === GuidStyle.Numeric) {
        theGuid = theGuid.replace(/-/g, "");
    }

    return theGuid;
}

/**
 * Pass in the objects to merge as arguments.
 * @param obj1 - object to merge.  Set this argument to 'true' for a deep extend.
 * @param obj2 - object to merge.
 * @param obj3 - object to merge.
 * @param obj4 - object to merge.
 * @param obj5 - object to merge.
 * @returns The extended object.
 */
export function extend(obj?: any, obj2?: any, obj3?: any, obj4?: any, obj5?: any): any {
    // Variables
    var extended = {};
    var deep = false;
    var i = 0;
    var length = arguments.length;
    var objProto = Object[Prototype];
    var theArgs = arguments;

    // Check if a deep merge
    if (objProto.toString.call(theArgs[0]) === "[object Boolean]") {
        deep = theArgs[0];
        i++;
    }

    // Loop through each object and conduct a merge
    for (; i < length; i++) {
        var obj = theArgs[i];
        objForEachKey(obj, (prop, value) => {
            // If deep merge and property is an object, merge properties
            if (deep && value && isObject(value)) {
                extended[prop] = extend(true, extended[prop], value);
            } else {
                extended[prop] = value;
            }
        });
    }

    return extended;
}

export let getTime = CoreUtils.perfNow;

function twoDigit(n: number): string {
    // Format integers to have at least two digits.
    return n < 10 ? "0" + n : n.toString();
}

function threeDigit(n: number): string {
    // Format integers to have at least three digits.
    if (n < 10) {
        return "00" + n;
    } else if (n < 100) {
        return "0" + n;
    }
    return n.toString();
}

export function isValueKind(value: number | undefined): boolean {
    if (isNumber(value) && ((value >= ValueKind.NotSet && value <= ValueKind.Pii_IPV4AddressLegacy) || value === ValueKind.CustomerContent_GenericContent)) {
        return true;
    }

    return false;
}

function isDataType(value: number): boolean {
    // Remark: 0 returns false, but it doesn't affect encoding anyways
    if (isNumber(value) && ((value >= 0 && value <= 9))) {
        return true;
    }
    return false;
}

function isSafariOrFirefox(): boolean {
    var nav = getNavigator();
    // If non-browser navigator will be undefined
    if (_isNotUndefined(nav) && nav.userAgent) {
        var ua = nav.userAgent.toLowerCase();
        if ((ua.indexOf("safari") >= 0 || ua.indexOf("firefox") >= 0) && ua.indexOf("chrome") < 0) {
            return true;
        }
    }
    return false;
}

export function isArrayValid(value: any[]): boolean {
    return value.length > 0;
}

export let objDefineAccessors = CoreUtils.objDefineAccessors;

export function addPageUnloadEventListener(listener: any): boolean {
    // Hook the unload event for the document, window and body to ensure that the client events are flushed to the server
    // As just hooking the window does not always fire (on chrome) for page navigations.
    let pageUnloadAdded = addEventHandler("beforeunload", listener);
    pageUnloadAdded = addEventHandler("unload", listener) || pageUnloadAdded;
    pageUnloadAdded = addEventHandler("pagehide", listener) || pageUnloadAdded;
    return pageUnloadAdded;
}

export function setProcessTelemetryTimings(event: ITelemetryItem, identifier: string): void {
    var evt = event as IExtendedTelemetryItem;
    evt.timings = evt.timings || {};
    evt.timings.processTelemetryStart = evt.timings.processTelemetryStart || {};
    evt.timings.processTelemetryStart[identifier] = getTime();
}

/**
 * Trys to add an event handler for the specified event to the window, body and document
 * @param eventName {string} - The name of the event
 * @param callback {any} - The callback function that needs to be executed for the given event
 * @returns true if the handler was successfully added
 */
export let addEventHandler = CoreUtils.addEventHandler;

/**
 * Returns a bitwise value for the FieldValueSanitizerType enum representing the decoded type of the passed value
 * @param value The value to determine the type
 */
export function getFieldValueType(value: any): FieldValueSanitizerType {
    let theType: FieldValueSanitizerType = FieldValueSanitizerType.NotSet;

    if (value !== null && value !== undefined) {
        let objType = typeof value;
        if (objType === "string") {
            theType = FieldValueSanitizerType.String;
        } else if (objType === "number") {
            theType = FieldValueSanitizerType.Number;
        } else if (objType === "boolean") {
            theType = FieldValueSanitizerType.Boolean;
        } else if (objType === strShimObject) {
            theType = FieldValueSanitizerType.Object;
            if (isArray(value)) {
                theType = FieldValueSanitizerType.Array;
                if (value.length > 0) {
                    // Empty arrays are not supported and are considered to be the same as null
                    theType |= getFieldValueType(value[0]);
                }
            } else if (_hasOwnProperty(value, "value")) {
                // Looks like an IEventProperty
                theType = FieldValueSanitizerType.EventProperty | getFieldValueType(value.value);
            }
        }
    }

    return theType;
}

/**
 * Returns a boolean to denote presence of suffix.
 * @param sourceString String to search in
 * @param suffix Suffix to identify
 * @returns `true` if the suffix is present, `false` if either `sourceString` or `suffix` are undefined of `suffix` is not present.
 */
export function strEndsWith(sourceString: string, suffix: string): boolean {
    return (sourceString !== undefined && suffix !== undefined) && sourceString.indexOf(suffix, sourceString.length - suffix.length) !== -1;
}
